import { TitleCasePipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ClickOutsideHandler } from '@core/services';
import { Subject, takeUntil } from 'rxjs';
import { PipesModule } from 'src/app/core/pipes/pipes.module';
import { ButtonModule } from 'src/app/project/components/button/button.module';
import { IconModule } from 'src/app/project/components/icon/icon-module';
import { DatepickerModule } from 'src/app/project/components/input/datepicker/datepicker.module';
import { OnRenderedModule } from 'src/app/project/directives/on-rendered/on-rendered.module';
import { TooltipsModule } from 'src/app/project/features/tooltip/tooltips.module';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { TSearchFilter } from '../../../filters-advanced/models/advanced-filter.model';
import { QuickReminderFilterService } from '../../../filters/filters-api/reminders-search/quick-reminder-filter.service';
import { FiltersModule } from '../../../filters/filters.module';
import { siteFilterClickOutside } from '../../../filters/site-filter-dropdown/site-filter-dropdown-utils/site-filter-click-outside';
import { EPointSearchOperator } from '../../../points/point-search/point-search.consts';
import {
  EReminderFilterType,
  TReminderFilterDueDate,
  TReminderFilterOptions,
} from '../reminder-filters.consts';
import {
  getReminderLinkedToOptions,
  getReminderStatusOptions,
  getReminderTypeOptions,
} from './reminder-filter-dropdown.component.utils';
import { ReminderOptionsListComponent } from './reminder-options-list/reminder-options-list.component';

@Component({
  selector: 'pp-reminder-filter-dropdown',
  templateUrl: './reminder-filter-dropdown.component.html',
  styleUrls: ['./reminder-filter-dropdown.component.scss'],
  imports: [
    ButtonModule,
    FiltersModule,
    PipesModule,
    IconModule,
    ReminderOptionsListComponent,
    OnRenderedModule,
    TooltipsModule,
    TitleCasePipe,
    DatepickerModule,
  ],
})
export class ReminderFilterDropdownComponent implements OnInit, OnDestroy {
  isDropdownVisible: boolean;
  filterButtonHovered = false;
  EIconPath = EIconPath;
  displayedSelectedFiltersValue = '';

  private readonly destroy$ = new Subject<void>();

  private clickOutsideHandler: ClickOutsideHandler;
  badgeIcon = iconMap[EIconPath.ICON_BADGE_INFO];

  typeOptions = getReminderTypeOptions();
  statusOptions = getReminderStatusOptions();
  linkedToOptions = getReminderLinkedToOptions();

  dueDateOptions: TReminderFilterDueDate = {
    from: null,
    to: null,
  };

  constructor(private quickReminderFilterService: QuickReminderFilterService) {
    this.quickReminderFilterService
      .getFiltersChange$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.displayedSelectedFiltersValue = this.getDisplayedSelectedFiltersValue();
      });
  }

  @HostListener('document:keydown.esc')
  onEscPress(): void {
    if (this.isDropdownVisible) {
      this.hideDropdown(true);
    }
  }

  ngOnInit() {
    this.isDropdownVisible = false;
    this.displayedSelectedFiltersValue = this.getDisplayedSelectedFiltersValue();
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  onFilterDropdownRendered(filterDropdownElement: HTMLElement): void {
    this.clickOutsideHandler = new ClickOutsideHandler(filterDropdownElement, this.destroy$, {
      mouseEventTypes: 'mousedown',
    });
    this.clickOutsideHandler.caught$.subscribe((event) => {
      this.onClickOutside(event);
    });
  }

  toggleDropdown(): void {
    if (this.isDropdownVisible) {
      this.hideDropdown(true);
    } else {
      this.showDropdown();
    }
  }

  hideDropdown(apply: boolean = false): void {
    this.isDropdownVisible = false;

    this.clickOutsideHandler.disable();

    if (!apply) {
      this.quickReminderFilterService.resetFilters();
      this.displayedSelectedFiltersValue = '';
    } else {
      this.applyFilters();
    }
  }

  applyFilters(): void {
    const filters = this.transformInputsToFilter();

    this.quickReminderFilterService.setFilters(filters);
    this.displayedSelectedFiltersValue = this.getDisplayedSelectedFiltersValue();
  }

  clearFilters(): void {
    logEventInGTAG(EGoogleEventSite.SITE__FILTER__CLEAR_ALL, {
      source: EGoogleEventCategory.REMINDERS,
    });

    this.hideDropdown();
  }

  filterButtonHover(): void {
    this.filterButtonHovered = true;
  }

  filterButtonHoverEnd(): void {
    this.filterButtonHovered = false;
  }

  showDropdown(): void {
    const filters = this.quickReminderFilterService.getFilters();
    this.transformFilterToInputs(filters);

    this.isDropdownVisible = true;

    this.clickOutsideHandler.enable();
  }

  private onClickOutside(event: MouseEvent): void {
    const shouldHideDropdown = siteFilterClickOutside(event);

    if (shouldHideDropdown) {
      event.preventDefault();
      event.stopPropagation();

      this.hideDropdown(true);
    }
  }

  getDisplayedSelectedFiltersValue(): string {
    const filters = this.quickReminderFilterService.getFilters();

    if (!filters) {
      return '';
    }

    let numberOfAppliedFilters = 0;

    filters.forEach((filter) => {
      switch (filter.keys[0]) {
        case EReminderFilterType.TYPE:
          if (filter.values.length !== this.typeOptions.options.length) {
            numberOfAppliedFilters++;
          }
          break;
        case EReminderFilterType.STATUS:
          if (filter.values.length !== this.statusOptions.options.length) {
            numberOfAppliedFilters++;
          }
          break;
        case EReminderFilterType.LINKED_TO:
          if (filter.values.length !== this.linkedToOptions.options.length) {
            numberOfAppliedFilters++;
          }
          break;
        case EReminderFilterType.DUE_DATE:
          numberOfAppliedFilters++;
          break;
      }
    });

    if (numberOfAppliedFilters > 0) {
      return ' / ' + numberOfAppliedFilters.toString();
    }

    return '';
  }

  onDateChange(date: Date[], type: keyof TReminderFilterDueDate): void {
    this.dueDateOptions = { ...this.dueDateOptions, [type]: +date[0].getTime() };
  }

  private transformFilterToInputs(filters: TSearchFilter[]): void {
    const typeOptions: TReminderFilterOptions = {
      ...this.typeOptions,
    };
    const statusOptions: TReminderFilterOptions = { ...this.statusOptions };
    const linkedToOptions: TReminderFilterOptions = { ...this.linkedToOptions };
    typeOptions.options.forEach((option) => {
      option.selected = true;
    });
    statusOptions.options.forEach((option) => {
      option.selected = true;
    });
    linkedToOptions.options.forEach((option) => {
      option.selected = true;
    });
    const dueDateOptions = {
      from: null,
      to: null,
    };

    filters.forEach((filter) => {
      const filterType = filter.keys[0];

      switch (filterType) {
        case EReminderFilterType.TYPE:
          typeOptions.options.forEach((option) => {
            option.selected = filter.values.includes(option.value);
          });
          break;
        case EReminderFilterType.STATUS:
          statusOptions.options.forEach((option) => {
            option.selected = filter.values.includes(option.value);
          });
          break;
        case EReminderFilterType.LINKED_TO:
          linkedToOptions.options.forEach((option) => {
            option.selected = filter.values.includes(option.value);
          });
          break;
        case EReminderFilterType.DUE_DATE: {
          const type = filter.operator;

          if (type === EPointSearchOperator.GREATER_THAN) {
            const fromValue: number =
              typeof filter.values[0] === 'number' ? filter.values[0] : null;
            dueDateOptions.from = fromValue ? fromValue : null;
            dueDateOptions.to = null;
          } else if (type === EPointSearchOperator.LESS_THAN) {
            const toValue: number = typeof filter.values[0] === 'number' ? filter.values[0] : null;
            dueDateOptions.to = toValue ? toValue : null;
            dueDateOptions.from = null;
          } else if (type === EPointSearchOperator.BETWEEN) {
            const fromValue: number =
              typeof filter.values[0] === 'number' ? filter.values[0] : null;
            const toValue: number = typeof filter.values[1] === 'number' ? filter.values[1] : null;
            dueDateOptions.from = fromValue ? fromValue : null;
            dueDateOptions.to = toValue ? toValue : null;
          }
          break;
        }
      }
    });

    this.typeOptions = typeOptions;
    this.statusOptions = statusOptions;
    this.linkedToOptions = linkedToOptions;
    this.dueDateOptions = dueDateOptions;

    this.displayedSelectedFiltersValue = this.getDisplayedSelectedFiltersValue();
  }

  private transformInputsToFilter(): TSearchFilter[] {
    const filters: TSearchFilter[] = [];

    const typeSelectedValues = this.typeOptions.options
      .filter((option) => option.selected)
      .map((option) => option.value);

    filters.push({
      keys: [EReminderFilterType.TYPE],
      values: typeSelectedValues,
      operator: EPointSearchOperator.IN,
    });

    const statusSelectedValues = this.statusOptions.options
      .filter((option) => option.selected)
      .map((option) => option.value);

    filters.push({
      keys: [EReminderFilterType.STATUS],
      values: statusSelectedValues,
      operator: EPointSearchOperator.IN,
    });

    const linkedToSelectedValues = this.linkedToOptions.options
      .filter((option) => option.selected)
      .map((option) => option.value);

    filters.push({
      keys: [EReminderFilterType.LINKED_TO],
      values: linkedToSelectedValues,
      operator: EPointSearchOperator.IN,
    });

    if (this.dueDateOptions.from || this.dueDateOptions.to) {
      if (this.dueDateOptions.from && !this.dueDateOptions.to) {
        filters.push({
          keys: [EReminderFilterType.DUE_DATE],
          values: [this.dueDateOptions.from],
          operator: EPointSearchOperator.GREATER_THAN,
        });
      } else if (!this.dueDateOptions.from && this.dueDateOptions.to) {
        filters.push({
          keys: [EReminderFilterType.DUE_DATE],
          values: [this.dueDateOptions.to],
          operator: EPointSearchOperator.LESS_THAN,
        });
      } else {
        filters.push({
          keys: [EReminderFilterType.DUE_DATE],
          values: [this.dueDateOptions.from, this.dueDateOptions.to],
          operator: EPointSearchOperator.BETWEEN,
        });
      }
    }

    return filters;
  }
}
